/**
 * Phase 6e.2 — per-(date, currency) rate lookup with T-1 fallback.
 *
 * Audit convention: "use the most recent published rate as of this
 * date". So when the exact requested date is missing (weekend,
 * holiday, or simply absent from the API response), walk BACKWARDS
 * one calendar day at a time until we find a published rate.
 *
 * The walk is bounded by `MAX_FALLBACK_DAYS` (14). Any gap longer
 * than two weeks is impossibly long for TCMB and almost certainly
 * indicates a backend / data problem rather than a holiday cluster.
 * Beyond the bound, returns `null` so the matcher records an error
 * for the auditor to investigate.
 *
 * The walk is one-directional (backwards only). Forward-looking
 * would violate the "as of" semantics — an auditor closing on
 * 31-Dec must not borrow a 2-Jan rate.
 */

import type { IsoDate, RateLookupResult, RatesByDate } from './types';

export const MAX_FALLBACK_DAYS = 14;

export function lookupRate(
  ratesIndex: RatesByDate,
  requestedDate: IsoDate,
  currency: string,
): RateLookupResult | null {
  const code = currency.toUpperCase();
  let cursor = requestedDate;
  for (let fellBackDays = 0; fellBackDays <= MAX_FALLBACK_DAYS; fellBackDays++) {
    const dayRates = ratesIndex[cursor];
    if (dayRates) {
      const value = dayRates[code];
      // A 0 / negative / non-finite rate is not a usable rate — treat it
      // as "no rate published this day" and keep walking back, same as a
      // missing entry. Mirrors the `rate > 0` guard in
      // `lookupTerminationFxRates`.
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        return {
          rate: value,
          date: cursor,
          requestedDate,
          fellBackDays,
        };
      }
    }
    cursor = subtractOneDay(cursor);
  }
  return null;
}

function subtractOneDay(iso: IsoDate): IsoDate {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
